import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import { fetchGhostSso } from "./fetchGhostSso";
import type { GhostEmbedProps, GhostEmbedRef } from "./types";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/lib/store/authStore";
import { FileText, AlertCircle, RefreshCw } from "lucide-react";
import { AnimatedStarburst } from "@/components/icons/AnimatedStarburst";

// Session storage keys — scoped per user to prevent cross-user leaks.
const GHOST_AUTH_SESSION_PREFIX = "ghost_authenticated_";

function isGhostAuthenticatedThisSession(userEmail: string): boolean {
  try {
    return (
      sessionStorage.getItem(GHOST_AUTH_SESSION_PREFIX + userEmail) === "true"
    );
  } catch {
    return false;
  }
}

function markGhostAuthenticated(userEmail: string): void {
  try {
    sessionStorage.setItem(GHOST_AUTH_SESSION_PREFIX + userEmail, "true");
  } catch {
    // Ignore
  }
}

/**
 * Clear all Ghost session flags from sessionStorage.
 * Called on logout to ensure next /blog visit triggers fresh SSO auth.
 */
export function clearGhostSession(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(GHOST_AUTH_SESSION_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Ignore
  }
}

/**
 * CSS to inject into Ghost Admin iframe to hide nav elements that
 * conflict with our dashboard shell (View site link, site name/logo).
 */
const GHOST_CSS_OVERRIDES = `
  /* Hide "View site" link — navigates outside iframe */
  .gh-nav-bottom .gh-nav-bottom-tabicon:has(svg[viewBox="0 0 24 24"]):first-child,
  a[href="#/site"],
  .gh-nav-viewsite { display: none !important; }

  /* Hide Ghost site name/logo in top-left — we have our own nav */
  .gh-nav-menu-details-sitetitle,
  .gh-nav-menu-details-blog { display: none !important; }
`;

export const GhostEmbed = forwardRef<GhostEmbedRef, GhostEmbedProps>(
  function GhostEmbed(props, ref) {
    const { className, initialPath, onReady, onRouteChange, onError } = props;

    const iframeRef = useRef<HTMLIFrameElement | null>(null);

    // Track SSO flow: Ghost SSO endpoint returns 302 → /ghost/.
    // Browser follows redirect → only ONE onLoad fires (final page).
    const isSsoFlow = useRef(false);
    const ssoTimeoutRef = useRef<number | null>(null);

    const [iframeSrc, setIframeSrc] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [iframeKey, setIframeKey] = useState(0);
    const [iframeReady, setIframeReady] = useState(false);

    const currentTenant = useAuthStore((state) => state.currentTenant);
    const username = useAuthStore((state) => state.username);
    const isProvisioned = useAuthStore((state) => state.isProvisioned);

    // Ghost Admin base URL (no SSO — direct dashboard load for cached sessions)
    const ghostBaseUrl = "https://blog.zaviago.com/ghost/";
    // Append initialPath for deep linking (e.g., "#/editor/post/abc123")
    const ghostAdminUrl = initialPath
      ? `${ghostBaseUrl}${initialPath}`
      : ghostBaseUrl;

    useEffect(() => {
      if (!currentTenant || !username) {
        setLoadError("Missing tenant or user information");
        setIsLoading(false);
        return;
      }

      // Check if already authenticated this session
      if (isGhostAuthenticatedThisSession(username)) {
        isSsoFlow.current = false;
        
        setIframeSrc(ghostAdminUrl);
        setIsLoading(false);
        return;
      }

      // Try SSO first, fall back to direct Ghost Admin URL on failure.
      // Ghost SSO endpoint may not exist yet (stock Ghost install).
      let cancelled = false;
      setIsLoading(true);
      setLoadError(null);

      fetchGhostSso(currentTenant, username)
        .then((data) => {
          if (cancelled) return;
          isSsoFlow.current = true;
          
          setIframeSrc(data.sso_url);
          setIsLoading(false);

          // Timeout: if SSO doesn't complete within 8s (2nd onLoad), fall back
          ssoTimeoutRef.current = window.setTimeout(() => {
            if (!iframeReady) {
              console.warn("[GhostEmbed] SSO timed out, loading Ghost Admin directly");
              isSsoFlow.current = false;
              
              setIframeSrc(ghostAdminUrl);
              setIframeKey((prev) => prev + 1);
            }
          }, 8000);
        })
        .catch((err) => {
          if (cancelled) return;
          // CP SSO API failed — fall back to direct Ghost Admin
          console.warn("[GhostEmbed] SSO fetch error, loading Ghost Admin directly:", err);
          isSsoFlow.current = false;
          
          setIframeSrc(ghostAdminUrl);
          setIsLoading(false);
        });

      return () => {
        cancelled = true;
        if (ssoTimeoutRef.current) clearTimeout(ssoTimeoutRef.current);
      };
    }, [currentTenant, username, onError]);

    // Inject CSS overrides into Ghost Admin iframe
    const injectCssOverrides = useCallback(() => {
      try {
        const iframe = iframeRef.current;
        if (!iframe?.contentDocument) return;
        const existingStyle =
          iframe.contentDocument.getElementById("zvg-ghost-overrides");
        if (existingStyle) return;
        const style = iframe.contentDocument.createElement("style");
        style.id = "zvg-ghost-overrides";
        style.textContent = GHOST_CSS_OVERRIDES;
        iframe.contentDocument.head.appendChild(style);
      } catch {
        // Cross-origin — CSS injection won't work, that's OK.
        // Ghost Admin is on blog.zaviago.com which is a different origin.
        // We'll handle this via a proxy or Ghost theme customization later.
      }
    }, []);

    // PostMessage listener for Ghost Admin route changes.
    // Ghost fork (when deployed) posts { type: "GHOST_ROUTE_CHANGE", path, title }.
    useEffect(() => {
      if (!onRouteChange) return;

      function handleMessage(event: MessageEvent) {
        // Origin check: only accept from Ghost Admin
        if (event.origin !== "https://blog.zaviago.com") return;
        const data = event.data;
        if (
          data &&
          typeof data === "object" &&
          data.type === "GHOST_ROUTE_CHANGE" &&
          typeof data.path === "string"
        ) {
          onRouteChange!(data.path, data.title || "");
        }
      }

      window.addEventListener("message", handleMessage);
      return () => window.removeEventListener("message", handleMessage);
    }, [onRouteChange]);

    // Navigate Ghost Admin to a specific path (for back/forward sync).
    // Posts GHOST_NAVIGATE message to the iframe.
    const navigateGhost = useCallback(
      (path: string) => {
        try {
          iframeRef.current?.contentWindow?.postMessage(
            { type: "GHOST_NAVIGATE", path },
            "https://blog.zaviago.com"
          );
        } catch {
          // Cross-origin restriction — Ghost fork must handle PostMessage
        }
      },
      []
    );

    const handleIframeLoad = useCallback(() => {
      if (ssoTimeoutRef.current) {
        clearTimeout(ssoTimeoutRef.current);
        ssoTimeoutRef.current = null;
      }
      setIframeReady(true);
      markGhostAuthenticated(username ?? "");
      injectCssOverrides();
      onReady?.();
    }, [onReady, username, injectCssOverrides]);

    const handleIframeError = useCallback(() => {
      setLoadError("Failed to load Blog editor. Please check your connection.");
      onError?.("iframe load error");
    }, [onError]);

    // Force reload with fresh SSO
    const reload = useCallback(() => {
      setLoadError(null);
      setIframeReady(false);
      setIframeSrc(null);
      setIsLoading(true);

      if (!currentTenant || !username) {
        setLoadError("Missing tenant or user information");
        setIsLoading(false);
        return;
      }

      isSsoFlow.current = true;
      
      fetchGhostSso(currentTenant, username)
        .then((data) => {
          setIframeSrc(data.sso_url);
          setIsLoading(false);
          setIframeKey((prev) => prev + 1);
        })
        .catch((err) => {
          console.error("[GhostEmbed] SSO fetch error:", err);
          setLoadError(
            err instanceof Error ? err.message : "Failed to get SSO URL"
          );
          setIsLoading(false);
          onError?.(err instanceof Error ? err.message : "SSO error");
        });
    }, [currentTenant, username, onError]);

    useImperativeHandle(
      ref,
      () => ({ reload, isReady: iframeReady, navigate: navigateGhost }),
      [reload, iframeReady, navigateGhost]
    );

    // Not provisioned state
    if (!isProvisioned) {
      return (
        <div
          className={cn(
            "flex flex-col items-center justify-center h-full p-8 text-center bg-gray-50",
            className
          )}
        >
          <FileText className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            Blog Not Available
          </h2>
          <p className="text-gray-500 max-w-md">
            Blog has not been set up for your workspace. Please contact your
            administrator.
          </p>
        </div>
      );
    }

    // Loading state
    if (isLoading) {
      return (
        <div
          className={cn(
            "flex flex-col items-center justify-center h-full bg-gray-50",
            className
          )}
        >
          <AnimatedStarburst size={48} className="mb-4" />
          <p className="text-gray-500">Loading blog editor...</p>
        </div>
      );
    }

    // Error state
    if (loadError) {
      return (
        <div
          className={cn(
            "flex flex-col items-center justify-center h-full p-8 text-center bg-gray-50",
            className
          )}
        >
          <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            Unable to load blog editor
          </h2>
          <p className="text-gray-500 max-w-md mb-4">{loadError}</p>
          <button
            onClick={reload}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </div>
      );
    }

    // No URL
    if (!iframeSrc) {
      return (
        <div
          className={cn(
            "flex flex-col items-center justify-center h-full p-8 text-center bg-gray-50",
            className
          )}
        >
          <AlertCircle className="w-16 h-16 text-yellow-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            Unable to Connect
          </h2>
          <p className="text-gray-500 max-w-md mb-4">
            Unable to connect to blog editor. Please try again.
          </p>
          <button
            onClick={reload}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </div>
      );
    }

    // Render iframe
    return (
      <div
        className={cn(
          "ghost-embed-wrapper relative w-full h-full min-h-[400px] bg-gray-50",
          className
        )}
      >
        {!iframeReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
            <div className="flex flex-col items-center">
              <AnimatedStarburst size={48} className="mb-4" />
              <p className="text-gray-500">Connecting to blog editor...</p>
            </div>
          </div>
        )}
        <iframe
          key={iframeKey}
          ref={iframeRef}
          src={iframeSrc}
          className="absolute inset-0 w-full h-full border-none"
          title="Ghost Blog Editor"
          allow="clipboard-read; clipboard-write"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
      </div>
    );
  }
);

GhostEmbed.displayName = "GhostEmbed";
